// UI Controls Handler

class Controls {
  constructor(viewer) {
    this.viewer = viewer;
    this.elements = {};
    this.currentUserId = '';
    this.currentUserRoot = '';
    this.folderDisplayNames = {};  // Persistent map: internal_name -> display_name
    this.initialize();
  }

  initialize() {
    // Get UI elements
    this.elements = {
      // Header
      btnDownload: document.getElementById('btnDownload'),
      btnSaveView: document.getElementById('btnSaveView'),
      btnRecordVideo: document.getElementById('btnRecordVideo'),
      btnSettings: document.getElementById('btnSettings'),

      // Modals
      fileModal: document.getElementById('fileModal'),
      openfoamModal: document.getElementById('openfoamModal'),
      renameCaseModal: document.getElementById('renameCaseModal'),
      btnCloseFile: document.getElementById('btnCloseFile'),
      btnCloseOpenFoam: document.getElementById('btnCloseOpenFoam'),
      btnCloseRename: document.getElementById('btnCloseRename'),
      btnCancelOpenFoam: document.getElementById('btnCancelOpenFoam'),
      btnCancelRename: document.getElementById('btnCancelRename'),
      btnSaveRename: document.getElementById('btnSaveRename'),
      renameCaseInput: document.getElementById('renameCaseInput'),
      renameCurrentName: document.getElementById('renameCurrentName'),

      // File input
      fileInput: document.getElementById('fileInput'),
      folderInput: document.getElementById('folderInput'),
      btnUploadFolder: document.getElementById('btnUploadFolder'),
      btnUploadFile: document.getElementById('btnUploadFile'),
      dropzone: document.getElementById('dropzone'),
      btnOpenFoamCase: document.getElementById('btnOpenFoamCase'),

      // OpenFOAM modal
      openfoamCasePath: document.getElementById('openfoamCasePath'),
      btnBrowseCases: document.getElementById('btnBrowseCases'),
      btnHomeDirectory: document.getElementById('btnGoHome'),
      btnLoadOpenFoam: document.getElementById('btnLoadOpenFoam'),
      caseInfo: document.getElementById('caseInfo'),
      caseTimeSteps: document.getElementById('caseTimeSteps'),
      caseFields: document.getElementById('caseFields'),
      fileBrowser: document.getElementById('fileBrowser'),
      breadcrumb: document.getElementById('breadcrumb'),
      fileList: document.getElementById('fileList'),

      // Viewport toolbar
      btnResetCamera: document.getElementById('btnResetCamera'),
      btnViewXY: document.getElementById('btnViewXY'),
      btnViewXZ: document.getElementById('btnViewXZ'),
      btnViewYZ: document.getElementById('btnViewYZ'),
      btnProbe: document.getElementById('btnProbe'),
      bgColor: document.getElementById('bgColor'),

      // Colormap
      colormapVariable: document.getElementById('colormapVariable'),
      colormapPreset: document.getElementById('colormapPreset'),
      colormapMin: document.getElementById('colormapMin'),
      colormapMax: document.getElementById('colormapMax'),
      btnAutoRange: document.getElementById('btnAutoRange'),
      colormapPreview: document.getElementById('colormapPreview'),
      solidColorPicker: document.getElementById('solidColorPicker'),
      solidColorInput: document.getElementById('solidColorInput'),

      // Time controls
      btnFirstFrame: document.getElementById('btnFirstFrame'),
      btnPrevFrame: document.getElementById('btnPrevFrame'),
      btnPlayPause: document.getElementById('btnPlayPause'),
      btnNextFrame: document.getElementById('btnNextFrame'),
      btnLastFrame: document.getElementById('btnLastFrame'),
      timeSlider: document.getElementById('timeSlider'),
      currentTime: document.getElementById('currentTime'),
      totalTime: document.getElementById('totalTime'),

      // Stats
      statPoints: document.getElementById('statPoints'),
      statCells: document.getElementById('statCells'),
      statMemory: document.getElementById('statMemory'),
      statRenderTime: document.getElementById('statRenderTime'),
      fpsCounter: document.getElementById('fpsCounter'),

      // Sidebar toggle
      sidebarToggle: document.getElementById('sidebarToggle'),
      mainContent: document.querySelector('.main-content'),
      coordinates: document.getElementById('coordinates'),

      // Pipeline
      pipelineTree: document.getElementById('pipelineTree'),
      btnAddFilter: document.getElementById('btnAddFilter'),

      // Filters
      filterBtns: document.querySelectorAll('.filter-btn')
    };

    this.recordButtonIcons = {
      start: this.elements.btnRecordVideo ? this.elements.btnRecordVideo.innerHTML : '',
      stop: '<svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor"><rect x="6" y="6" width="8" height="8" rx="1"/></svg>'
    };

    this.setupEventListeners();
    this.updateColormapPreview();
    this.setRecordingState(false);
  }

  setupEventListeners() {
    // Header buttons
    this.elements.btnDownload.addEventListener('click', () => this.downloadCurrentSimulation());
    this.elements.btnSaveView.addEventListener('click', () => this.viewer.saveScreenshot());
    if (this.elements.btnRecordVideo) {
      this.elements.btnRecordVideo.addEventListener('click', () => this.viewer.toggleVideoRecording());
    }

    // Modal controls
    this.elements.btnCloseFile.addEventListener('click', () => this.hideFileModal());
    this.elements.btnCloseOpenFoam.addEventListener('click', () => this.hideOpenFoamModal());
    this.elements.btnCancelOpenFoam.addEventListener('click', () => this.hideOpenFoamModal());

    // File input
    this.elements.fileInput.addEventListener('change', (e) => this.handleFileSelect(e));
    this.elements.folderInput.addEventListener('change', (e) => this.handleFolderSelect(e));
    this.elements.btnUploadFolder.addEventListener('click', () => this.elements.folderInput.click());
    this.elements.btnUploadFile.addEventListener('click', () => this.elements.fileInput.click());
    this.elements.dropzone.addEventListener('dragover', (e) => this.handleDragOver(e));
    this.elements.dropzone.addEventListener('drop', (e) => this.handleDrop(e));

    // OpenFOAM modal
    this.elements.btnOpenFoamCase.addEventListener('click', () => this.showOpenFoamModal());
    this.elements.btnLoadOpenFoam.addEventListener('click', () => this.handleLoadOpenFoam());
    this.elements.openfoamCasePath.addEventListener('input', () => {
      delete this.elements.openfoamCasePath.dataset.fullPath;
      delete this.elements.openfoamCasePath.dataset.relativePath;
      this.handleCasePathChange();
    });
    this.elements.btnBrowseCases.addEventListener('click', () => this.showFileBrowser());
    this.elements.btnHomeDirectory.addEventListener('click', () => this.goToHomeDirectory());

    // Use event delegation for file list (prevents duplicate listeners)
    this.elements.fileList.addEventListener('click', async (e) => {
      const deleteBtn = e.target.closest('.file-item-delete');
      const downloadBtn = e.target.closest('.file-item-download');
      const renameBtn = e.target.closest('.file-item-rename');
      const fileItem = e.target.closest('.file-item');

      if (deleteBtn) {
        e.stopPropagation();
        const relativePath = (deleteBtn.dataset.relativePath || '').replace(/&quot;/g, '"');
        const itemName = deleteBtn.dataset.name || 'this item';
        await this.handleDeleteItem(relativePath, itemName);
      } else if (downloadBtn) {
        e.stopPropagation();
        const relativePath = (downloadBtn.dataset.relativePath || '').replace(/&quot;/g, '"');
        const itemName = downloadBtn.dataset.name || 'this folder';
        await this.handleDownloadFolder(relativePath, itemName);
      } else if (renameBtn) {
        e.stopPropagation();
        const fullPath = (renameBtn.dataset.path || '').replace(/&quot;/g, '"');
        const relativePath = (renameBtn.dataset.relativePath || '').replace(/&quot;/g, '"');
        const currentName = renameBtn.dataset.name || '';
        await this.showRenameModal(fullPath, relativePath, currentName);
      } else if (fileItem && !e.target.closest('button')) {
        // Highlight selection
        this.elements.fileList.querySelectorAll('.file-item').forEach(i => i.classList.remove('selected'));
        fileItem.classList.add('selected');

        const fullPath = (fileItem.dataset.fullPath || '').replace(/&quot;/g, '"');
        const relativePath = (fileItem.dataset.relativePath || '').replace(/&quot;/g, '"');
        const isCase = fileItem.dataset.isCase === 'true';
        const caseType = fileItem.dataset.caseType || 'openfoam';

        if (isCase) {
          // Show preview in path input
          this.selectCase(fullPath, relativePath, { preview: false, hideBrowser: false, caseType });
          this.handleCasePathChange();
        }
      }
    });

    // Handle double-click to navigate/load
    this.elements.fileList.addEventListener('dblclick', async (e) => {
      const fileItem = e.target.closest('.file-item');
      if (!fileItem || e.target.closest('button')) return;

      const fullPath = (fileItem.dataset.fullPath || '').replace(/&quot;/g, '"');
      const relativePath = (fileItem.dataset.relativePath || '').replace(/&quot;/g, '"');
      const isDir = fileItem.dataset.isDir === 'true';
      const isCase = fileItem.dataset.isCase === 'true';
      const caseType = fileItem.dataset.caseType || 'openfoam';

      if (isDir && !isCase) {
        // Navigate into directory
        await this.showFileBrowser(relativePath || null);
      } else if (isCase) {
        // Load case - type determines which loader to use
        this.selectCase(fullPath, relativePath, { caseType });
      }
    });

    // Rename modal
    this.elements.btnCloseRename.addEventListener('click', () => this.hideRenameModal());
    this.elements.btnCancelRename.addEventListener('click', () => this.hideRenameModal());
    this.elements.btnSaveRename.addEventListener('click', () => this.handleSaveRename());

    // Camera controls
    this.elements.btnResetCamera.addEventListener('click', () => this.viewer.resetCamera());
    this.elements.btnViewXY.addEventListener('click', () => {
      this.viewer.setCameraView('xy');
      this.setActiveViewButton('xy');
    });
    this.elements.btnViewXZ.addEventListener('click', () => {
      this.viewer.setCameraView('xz');
      this.setActiveViewButton('xz');
    });
    this.elements.btnViewYZ.addEventListener('click', () => {
      this.viewer.setCameraView('yz');
      this.setActiveViewButton('yz');
    });

    // Display controls
    this.elements.btnProbe.addEventListener('click', () => {
      this.viewer.enableProbe(!this.viewer.probeEnabled);
      this.elements.btnProbe.classList.toggle('active');
    });
    this.elements.bgColor.addEventListener('change', (e) => {
      this.viewer.setBackgroundColor(e.target.value);
    });

    // Colormap controls
    this.elements.colormapPreset.addEventListener('change', () => {
      this.updateColormapPreview();
      this.updateSolidColorPickerVisibility();
      if (this.viewer.colormap.autoRange !== false) {
        this.viewer.autoRangeColormap();
      } else {
        this.viewer.updateColormap();
      }
      // Notify view lock manager of visualization change
      if (this.viewer.viewLockManager) {
        this.viewer.viewLockManager.onVisualizationChange();
      }
    });

    // Solid color picker - debounce mesh updates for performance
    // Note: The colormap.js initializeAllSelects() handles the main picker events
    // This is a backup that uses the correct dropdown ID
    if (this.elements.solidColorInput) {
      let colorUpdateTimeout = null;
      const mainDropdownId = 'colormapPreset';

      // Fast preview update on input
      this.elements.solidColorInput.addEventListener('input', () => {
        Colormap.setSolidColor(this.elements.solidColorInput.value, mainDropdownId);
        this.updateColormapPreview();

        // Debounce the expensive mesh update
        if (colorUpdateTimeout) {
          clearTimeout(colorUpdateTimeout);
        }
        colorUpdateTimeout = setTimeout(() => {
          this.viewer.updateColormap();
        }, 100);
      });

      // Final update when user finishes selecting
      this.elements.solidColorInput.addEventListener('change', () => {
        if (colorUpdateTimeout) {
          clearTimeout(colorUpdateTimeout);
        }
        Colormap.setSolidColor(this.elements.solidColorInput.value, mainDropdownId);
        this.viewer.updateColormap();
        // Notify view lock manager of visualization change
        if (this.viewer.viewLockManager) {
          this.viewer.viewLockManager.onVisualizationChange();
        }
      });
    }
    this.elements.colormapVariable.addEventListener('change', () => {
      this.viewer.onColormapVariableChange();
      // Notify view lock manager of visualization change
      if (this.viewer.viewLockManager) {
        this.viewer.viewLockManager.onVisualizationChange();
      }
    });
    this.elements.colormapMin.addEventListener('change', () => {
      this.viewer.setManualColormapRange();
      this.viewer.updateColormap();
      // Notify view lock manager of visualization change
      if (this.viewer.viewLockManager) {
        this.viewer.viewLockManager.onVisualizationChange();
      }
    });
    this.elements.colormapMax.addEventListener('change', () => {
      this.viewer.setManualColormapRange();
      this.viewer.updateColormap();
      // Notify view lock manager of visualization change
      if (this.viewer.viewLockManager) {
        this.viewer.viewLockManager.onVisualizationChange();
      }
    });
    this.elements.btnAutoRange.addEventListener('click', () => {
      this.viewer.enableAutoColormapRange();
      this.viewer.autoRangeColormap();
      // Notify view lock manager of visualization change
      if (this.viewer.viewLockManager) {
        this.viewer.viewLockManager.onVisualizationChange();
      }
    });

    // Time controls
    this.elements.btnFirstFrame.addEventListener('click', () => this.viewer.goToFrame(0));
    this.elements.btnPrevFrame.addEventListener('click', () => this.viewer.previousFrame());
    this.elements.btnPlayPause.addEventListener('click', () => this.viewer.togglePlayback());
    this.elements.btnNextFrame.addEventListener('click', () => this.viewer.nextFrame());
    this.elements.btnLastFrame.addEventListener('click', () => this.viewer.goToLastFrame());
    this._sliderDebounceTimer = null;
    this.elements.timeSlider.addEventListener('input', (e) => {
      const frame = parseInt(e.target.value);
      // Update UI immediately (slider position + time label) without backend request
      this.viewer.setCurrentTimestep(frame, { refreshVisualization: false });
      // Debounce the actual geometry reload — only fire after user pauses scrubbing
      clearTimeout(this._sliderDebounceTimer);
      this._sliderDebounceTimer = setTimeout(() => {
        this.viewer.goToFrame(frame);
      }, 250);
    });

    // Playback speed slider
    const fpsSlider = document.getElementById('playbackFps');
    const fpsLabel = document.getElementById('playbackFpsValue');
    if (fpsSlider && fpsLabel) {
      fpsSlider.addEventListener('input', () => {
        const fps = parseInt(fpsSlider.value, 10);
        fpsLabel.textContent = fps + ' fps';
        this.viewer.playbackTargetFps = fps;
      });
    }

    // Sidebar toggle
    if (this.elements.sidebarToggle && this.elements.mainContent) {
      this.elements.sidebarToggle.addEventListener('click', () => this.toggleSidebar());

      // Restore sidebar state from localStorage
      const sidebarHidden = localStorage.getItem('coandacloud-sidebar-hidden') === 'true';
      if (sidebarHidden) {
        this.elements.mainContent.classList.add('sidebar-hidden');
      }
    }

    // Filter buttons
    this.elements.filterBtns.forEach(btn => {
      btn.addEventListener('click', async () => {
        const filterType = btn.dataset.filter;
        try {
          await this.viewer.applyFilter(filterType);
        } catch (error) {
          console.error('Failed to apply filter:', error);
          Utils.notify('Filter error: ' + (error?.message || error), 'error');
        }
      });
    });

    // Keyboard shortcuts
    document.addEventListener('keydown', (e) => this.handleKeyboard(e));
  }

  handleKeyboard(e) {
    // Ignore keyboard shortcuts when typing in input fields
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
      return;
    }

    if (e.ctrlKey || e.metaKey) {
      switch(e.key.toLowerCase()) {
        case 'o':
          e.preventDefault();
          this.showFileModal();
          break;
        case 's':
          e.preventDefault();
          this.viewer.saveScreenshot();
          break;
      }
    }

    switch(e.key) {
      case ' ':
        e.preventDefault();
        this.viewer.togglePlayback();
        break;
      case 'r':
        // Don't reset camera if slice or clip widget is enabled (R is for rotate mode)
        if (this.viewer.sliceWidget?.enabled) {
          return;
        }
        this.viewer.resetCamera();
        break;
      case 'g':
        // Don't interfere with translate mode shortcut for widgets
        if (this.viewer.sliceWidget?.enabled) {
          return;
        }
        break;
    }
  }

  async showFileModal() {
    this.elements.fileModal.style.display = 'flex';

    // Fetch and display storage information
    await this.updateStorageDisplay();
  }

  hideFileModal() {
    this.elements.fileModal.style.display = 'none';
  }

  async downloadCurrentSimulation() {
    const dataPath = this.viewer.sessionDataPath;

    if (!dataPath) {
      Utils.notify('No simulation is currently loaded', 'error');
      return;
    }

    // Use display name if available (has the actual filename like cavity.vtk)
    // Fall back to extracting from path
    const displayName = this.viewer.caseDisplayName || dataPath.split('/').filter(p => p).pop() || 'simulation';
    // Use shared utility function (perimeter 124 for 36x36 header buttons)
    await Utils.downloadFolderWithProgress(dataPath, displayName, this.elements.btnDownload, 124);
  }

  async getStorageInfo() {
    try {
      const apiBase = window.location.origin.includes('localhost')
        ? 'http://localhost:8082'
        : '';

      const token = localStorage.getItem('coandacloud_token');
      if (!token) {
        return null;
      }

      const response = await fetch(`${apiBase}/api/auth/storage`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        return null;
      }

      const result = await response.json();
      return result.status === 'success' ? result.data : null;
    } catch (error) {
      console.error('Error fetching storage info:', error);
      return null;
    }
  }

  async updateStorageDisplay() {
    try {
      const result = { status: 'success', data: await this.getStorageInfo() };

      if (result.status === 'success' && result.data) {
        const { used_bytes, total_bytes, used_percentage, quota_gb } = result.data;

        // Update progress bar
        const progressBar = document.getElementById('storageProgressBar');
        const usageText = document.getElementById('storageUsageText');
        const usedAmount = document.getElementById('storageUsedAmount');
        const totalAmount = document.getElementById('storageTotalAmount');

        if (progressBar) {
          progressBar.style.width = `${Math.min(used_percentage, 100)}%`;

          // Change color based on usage
          if (used_percentage >= 90) {
            progressBar.style.background = 'linear-gradient(90deg, #f44336, #d32f2f)'; // Red
          } else if (used_percentage >= 75) {
            progressBar.style.background = 'linear-gradient(90deg, #ff9800, #f57c00)'; // Orange
          } else if (used_percentage >= 50) {
            progressBar.style.background = 'linear-gradient(90deg, #ffc107, #ffa000)'; // Yellow
          } else {
            progressBar.style.background = 'linear-gradient(90deg, #4CAF50, #2196F3)'; // Green-Blue
          }
        }

        if (usageText) {
          usageText.textContent = `${used_percentage.toFixed(1)}% used`;
        }

        if (usedAmount) {
          usedAmount.textContent = this.formatBytes(used_bytes);
        }

        if (totalAmount) {
          totalAmount.textContent = `${quota_gb.toFixed(2)} GB`;
        }
      }
    } catch (error) {
      console.error('Failed to fetch storage info:', error);
      // Optionally show an error state
      const usageText = document.getElementById('storageUsageText');
      if (usageText) {
        usageText.textContent = 'Unable to load';
      }
    }
  }

  formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return '0 B';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  async handleFileSelect(e) {
    const files = e.target.files;
    if (files.length > 0) {
      await this.viewer.loadFile(files[0]);
      this.hideFileModal();
    }
  }

  async handleFolderSelect(e) {
    const files = e.target.files;
    if (files.length > 0) {
      try {
        // Get display name from input field
        const displayNameInput = document.getElementById('displayNameInput');
        const displayName = displayNameInput ? displayNameInput.value.trim() : null;

        // Upload folder to server with display name
        const uploader = new FolderUploader(this.viewer);
        const result = await uploader.uploadFolder(files, displayName);

        if (result.status === 'success') {
          const caseName = result.data.display_name || result.data.case_name;

          // Clear the display name input for next upload
          if (displayNameInput) {
            displayNameInput.value = '';
          }

          // Update storage display to reflect new usage
          await this.updateStorageDisplay();

          // Get current storage info for success modal
          const storageInfo = await this.getStorageInfo();

          // Show success modal with storage information
          FolderUploader.showUploadSuccess({
            caseName: caseName,
            fileCount: result.data.file_count,
            isOpenFOAM: result.data.is_openfoam,
            storageUsed: storageInfo ? storageInfo.used_bytes : 0,
            storageTotal: storageInfo ? storageInfo.total_bytes : 0
          });

          // If it's an OpenFOAM case, load it automatically
          if (result.data.is_openfoam) {
            await this.viewer.loadOpenFOAMWithGeometry(result.data.case_path);
          }

          this.hideFileModal();
        }
      } catch (error) {
        console.error('Folder upload error:', error);

        // Check if this is a storage quota error
        if (error.isQuotaExceeded) {
          FolderUploader.showUpgradePrompt(error.message);
        } else {
          Utils.notify(`Failed to upload folder: ${error.message}`, 'error');
        }
      }
    }
  }

  handleDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
    this.elements.dropzone.style.borderColor = 'var(--primary-color)';
  }

  async handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    this.elements.dropzone.style.borderColor = 'var(--border-color)';

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      await this.viewer.loadFile(files[0]);
      this.hideFileModal();
    }
  }

  updateConnectionStatus(status) {
    this.elements.connectionStatus.textContent = status.charAt(0).toUpperCase() + status.slice(1);
    this.elements.connectionStatus.className = `status-indicator ${status}`;
  }

  setActiveViewButton(view) {
    // Remove active class from all view buttons
    this.elements.btnViewXY.classList.remove('active');
    this.elements.btnViewXZ.classList.remove('active');
    this.elements.btnViewYZ.classList.remove('active');

    // Add active class to the selected view button
    if (view === 'xy') {
      this.elements.btnViewXY.classList.add('active');
    } else if (view === 'xz') {
      this.elements.btnViewXZ.classList.add('active');
    } else if (view === 'yz') {
      this.elements.btnViewYZ.classList.add('active');
    }
  }

  clearActiveViewButton() {
    // Remove active class from all view buttons (when user manually moves camera)
    this.elements.btnViewXY.classList.remove('active');
    this.elements.btnViewXZ.classList.remove('active');
    this.elements.btnViewYZ.classList.remove('active');
  }

  showOpenFoamModal() {
    this.hideFileModal();
    this.elements.openfoamModal.style.display = 'flex';
    this.elements.caseInfo.style.display = 'none';
    this.elements.fileBrowser.style.display = 'none';
  }

  hideOpenFoamModal() {
    this.elements.openfoamModal.style.display = 'none';
    this.elements.fileBrowser.style.display = 'none';
  }

  showRenameModal(fullPath, relativePath, currentName) {
    // Store the file information for the rename operation
    this.renameFileInfo = {
      fullPath,
      relativePath,
      currentName
    };

    // Set current name in modal
    this.elements.renameCurrentName.textContent = currentName;
    this.elements.renameCaseInput.value = currentName;

    // Show modal
    this.elements.renameCaseModal.style.display = 'flex';

    // Focus input and select all text
    setTimeout(() => {
      this.elements.renameCaseInput.focus();
      this.elements.renameCaseInput.select();
    }, 100);
  }

  hideRenameModal() {
    this.elements.renameCaseModal.style.display = 'none';
    this.elements.renameCaseInput.value = '';
    this.renameFileInfo = null;
  }

  async handleSaveRename() {
    if (!this.renameFileInfo) return;

    const newName = this.elements.renameCaseInput.value.trim();
    if (!newName) {
      Utils.notify('Please enter a name', 'error');
      return;
    }

    if (newName === this.renameFileInfo.currentName) {
      this.hideRenameModal();
      return;
    }

    try {
      const apiBase = window.location.origin.includes('localhost')
        ? 'http://localhost:8082'
        : '';

      // Get file_id from database by path
      const token = localStorage.getItem('coandacloud_token');
      if (!token) {
        throw new Error('Not authenticated');
      }

      // Call the rename API endpoint
      const response = await fetch(`${apiBase}/api/files/rename-by-path`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          file_path: this.renameFileInfo.fullPath,
          new_display_name: newName
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to rename');
      }

      const result = await response.json();
      Utils.notify(`Renamed to "${newName}"`, 'success');

      // Hide modal
      this.hideRenameModal();

      // Refresh file browser
      await this.showFileBrowser(this.currentBrowsePath || null);

    } catch (error) {
      console.error('Rename error:', error);
      Utils.notify(`Failed to rename: ${error.message}`, 'error');
    }
  }

  async showFileBrowser(path = null) {
    try {
      const result = await this.viewer.openfoamLoader.browseDirectory(path);
      if (result.status === 'success') {
        const userRoot = result.user_root || '';
        const userId = userRoot ? userRoot.split('/').filter(Boolean).pop() : 'uploads';
        const relativePath = result.relative_path || '';
        this.currentUserId = userId;
        this.currentUserRoot = userRoot;
        this.currentBrowsePath = relativePath;

        // Accumulate display names into persistent map
        if (result.items) {
          result.items.forEach(item => {
            if (item.internal_name && item.name) {
              // Store in persistent map for breadcrumb use
              this.folderDisplayNames[item.internal_name] = item.name;
            }
          });
        }

        // Store items for breadcrumb lookup
        this.currentItems = result.items || [];

        this.renderBreadcrumb({
          userId,
          relativePath,
          isRoot: result.is_root,
          parentPath: result.parent_path,
          displayNameMap: this.folderDisplayNames,  // Use persistent map
          items: result.items
        });
        this.renderFileList(result.items);
        this.elements.fileBrowser.style.display = 'block';
      }
    } catch(err) {
      Utils.notify(`Failed to browse directory: ${err.message}`, 'error');
    }
  }

  async goToHomeDirectory() {
    await this.showFileBrowser(null);
  }

  async handleDeleteItem(relativePath, itemName) {
    // Ask for confirmation
    if (!confirm(`Are you sure you want to delete "${itemName}"?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      const result = await this.viewer.openfoamLoader.deleteUpload(relativePath);
      if (result.status === 'success') {
        Utils.notify(`Deleted "${itemName}" successfully`, 'success');
        // Update storage display to reflect freed space
        await this.updateStorageDisplay();
        // Refresh the current directory view
        await this.showFileBrowser(this.currentBrowsePath || null);
      }
    } catch (err) {
      Utils.notify(`Failed to delete: ${err.message}`, 'error');
    }
  }

  async handleDownloadFolder(relativePath, itemName) {
    const overlay = document.getElementById('loadingOverlay');
    if (overlay) {
      overlay.querySelector('p').textContent = `Preparing download for "${itemName}"...`;
      overlay.style.display = 'flex';
    }
    try {
      await this.viewer.openfoamLoader.downloadFolder(relativePath || '');
      Utils.notify(`Downloading "${itemName}"...`, 'info');
    } catch (err) {
      console.error('Failed to download folder:', err);
      Utils.notify(`Failed to download: ${err.message}`, 'error');
    } finally {
      if (overlay) {
        overlay.style.display = 'none';
        overlay.querySelector('p').textContent = 'Loading data...';
      }
    }
  }

  setRecordingState(isRecording) {
    const btn = this.elements.btnRecordVideo;
    if (!btn) return;

    btn.classList.toggle('recording', !!isRecording);
    if (isRecording) {
      btn.innerHTML = this.recordButtonIcons.stop;
      btn.title = 'Stop Recording';
    } else {
      btn.innerHTML = this.recordButtonIcons.start;
      btn.title = 'Record Video';
    }
  }

  renderBreadcrumb({ userId, relativePath, isRoot, parentPath, displayNameMap = {} }) {
    const parts = (relativePath || '').split('/').filter(p => p);
    let breadcrumbHTML = '';

    if (!isRoot && parentPath !== undefined) {
      breadcrumbHTML += `<span class="breadcrumb-item" data-path="${parentPath}"><img src="static/assets/icons/arrow-up.svg" alt="" width="14" height="14" style="vertical-align: middle; filter: brightness(0) invert(1); opacity: 0.8;"> Up</span> / `;
    }

    // Root breadcrumb shows user's display name (or email if not available)
    const userInfo = window.Auth ? window.Auth.getUserInfo() : null;
    const userDisplayName = userInfo?.display_name || userInfo?.name || userId;
    breadcrumbHTML += `<span class="breadcrumb-item" data-path=""><img src="static/assets/icons/folder-case.svg" alt="" width="14" height="14" style="vertical-align: middle; filter: brightness(0) invert(1); opacity: 0.8;"> ${userDisplayName}</span>`;

    let cumulativePath = '';
    parts.forEach((part, index) => {
      cumulativePath += (cumulativePath ? '/' : '') + part;
      const isLast = index === parts.length - 1;

      // Use display name if available, otherwise use internal name
      const displayName = displayNameMap[part] || part;

      if (isLast) {
        breadcrumbHTML += ` / <span class="breadcrumb-current">${displayName}</span>`;
      } else {
        breadcrumbHTML += ` / <span class="breadcrumb-item" data-path="${cumulativePath}">${displayName}</span>`;
      }
    });

    this.elements.breadcrumb.innerHTML = breadcrumbHTML;

    // Add click handlers to breadcrumb items
    this.elements.breadcrumb.querySelectorAll('.breadcrumb-item').forEach(item => {
      item.addEventListener('click', () => {
        const path = item.dataset.path || null;
        this.showFileBrowser(path);
      });
    });
  }

  renderFileList(items) {
    if (!items || items.length === 0) {
      this.elements.fileList.innerHTML = '<div style="padding: 20px; text-align: center; color: #999;">Empty directory</div>';
      return;
    }

    let html = '<div class="file-list-grid">';

    items.forEach(item => {
      // Use SVG icons instead of emojis
      let iconSrc = 'static/assets/icons/document.svg';
      if (item.is_dir) iconSrc = 'static/assets/icons/folder-case.svg';
      if (item.is_case) {
        const ct = item.case_type || 'openfoam';
        if (ct === 'openfoam') iconSrc = 'static/assets/icons/openfoam.svg';
        else if (ct === 'fluent') iconSrc = 'static/assets/icons/fluent.svg';
        else if (ct === 'ensight') iconSrc = 'static/assets/icons/ensight.svg';
      }
      const icon = `<img src="${iconSrc}" alt="" width="18" height="18" style="vertical-align: middle; filter: brightness(0) invert(1); opacity: 0.9;">`;
      const classes = ['file-item'];
      const caseType = item.case_type || 'openfoam';

      if (item.is_case) classes.push('openfoam-case');
      if (item.is_dir) classes.push('directory');
      const fullPath = item.path;
      const relativePath = item.relative_path || '';
      const fullPathAttr = fullPath.replace(/"/g, '&quot;');
      const relativePathAttr = relativePath.replace(/"/g, '&quot;');
      const displayName = item.name;
      const sizeBytes = Number.isFinite(item.size) ? item.size : 0;
      const sizeLabel = sizeBytes > 0
        ? Utils.formatBytes(sizeBytes)
        : (item.is_dir ? 'Empty' : '0 B');

      // Determine badge text
      let badgeText = '';
      if (item.is_case) {
        if (caseType === 'fluent') badgeText = 'Fluent';
        else if (caseType === 'ensight') badgeText = 'EnSight';
        else badgeText = 'OpenFOAM';
      }

      const downloadButton = item.is_dir
        ? `<button class="file-item-download" data-relative-path="${relativePathAttr}" data-name="${displayName}" title="Download"><img src="static/assets/icons/download.svg" alt="" width="14" height="14" style="filter: brightness(0) invert(1);"></button>`
        : '';

      // Add rename button for directories/cases
      const renameButton = item.is_dir
        ? `<button class="file-item-rename" data-path="${fullPathAttr}" data-relative-path="${relativePathAttr}" data-name="${displayName}" title="Rename"><img src="static/assets/icons/edit.svg" alt="" width="14" height="14" style="filter: brightness(0) invert(1);"></button>`
        : '';

      html += `
        <div class="${classes.join(' ')}"
             data-full-path="${fullPathAttr}"
             data-relative-path="${relativePathAttr}"
             data-is-dir="${item.is_dir}"
             data-is-case="${item.is_case}"
             data-case-type="${caseType}">
          ${downloadButton}
          ${renameButton}
          <button class="file-item-delete" data-relative-path="${relativePathAttr}" data-name="${displayName}" title="Delete">&times;</button>
          <div class="file-icon">${icon}</div>
          <div class="file-name" title="${displayName}">${displayName}</div>
          ${item.is_case ? `<div class="case-badge">${badgeText}</div>` : ''}
          <div class="file-size">${sizeLabel}</div>
        </div>
      `;
    });

    html += '</div>';
    this.elements.fileList.innerHTML = html;

    // Event handlers are managed via event delegation in setupEventListeners()
    // This prevents duplicate listeners when renderFileList is called multiple times
  }

  selectCase(fullPath, relativePath = '', options = {}) {
    const { preview = true, hideBrowser = true, caseType = 'openfoam' } = options;

    // Build friendly display path using display names
    let displayPath = '';

    // Get user display name
    const userInfo = window.Auth ? window.Auth.getUserInfo() : null;
    const userDisplayName = userInfo?.display_name || userInfo?.name || this.currentUserId;

    if (relativePath) {
      // Split path and replace each part with display name if available
      const pathParts = relativePath.split('/').filter(p => p);
      const friendlyParts = pathParts.map(part => this.folderDisplayNames[part] || part);
      displayPath = `${userDisplayName}/${friendlyParts.join('/')}`;
    } else {
      displayPath = userDisplayName;
    }

    this.elements.openfoamCasePath.value = displayPath;
    this.elements.openfoamCasePath.dataset.fullPath = fullPath;
    this.elements.openfoamCasePath.dataset.relativePath = relativePath;
    this.elements.openfoamCasePath.dataset.caseType = caseType;
    if (hideBrowser) {
      this.elements.fileBrowser.style.display = 'none';
    }
    if (preview) {
      this.handleCasePathChange();
    }
  }

  async handleCasePathChange() {
    const input = this.elements.openfoamCasePath;
    const casePath = input.dataset.fullPath || this.resolveCasePathInput(input.value);
    const caseType = input.dataset.caseType || 'openfoam';

    if (!casePath) {
      this.elements.caseInfo.style.display = 'none';
      return;
    }

    // Only get case info for OpenFOAM cases
    if (caseType !== 'openfoam') {
      this.elements.caseInfo.style.display = 'none';
      return;
    }

    try {
      const info = await this.viewer.openfoamLoader.getCaseInfo(casePath);
      if (info.status === 'success') {
        input.dataset.fullPath = casePath;
        if (this.currentUserRoot && casePath.startsWith(this.currentUserRoot)) {
          const rel = casePath.slice(this.currentUserRoot.length).replace(/^\/+/, '');
          input.dataset.relativePath = rel;
        }
        this.elements.caseTimeSteps.textContent = info.n_times;
        this.elements.caseFields.textContent = info.fields.join(', ');
        this.elements.caseInfo.style.display = 'block';
      }
    } catch(err) {
      console.error('Error getting case info:', err);
      this.elements.caseInfo.style.display = 'none';
    }
  }

  async handleLoadOpenFoam() {
    const input = this.elements.openfoamCasePath;
    const casePath = input.dataset.fullPath || this.resolveCasePathInput(input.value);
    const caseType = input.dataset.caseType || 'openfoam';

    if (!casePath) {
      Utils.notify('Please enter a case path', 'warning');
      return;
    }

    try {
      // Load based on case type
      if (caseType === 'fluent') {
        await this.viewer.loadFluentCase(casePath);
      } else if (caseType === 'ensight') {
        console.log('[Controls] Loading EnSight case');
        await this.viewer.loadEnSightWithGeometry(casePath);
      } else {
        console.log('[Controls] Loading OpenFOAM case');
        await this.viewer.loadOpenFOAMWithGeometry(casePath);
      }
      this.hideOpenFoamModal();
    } catch(err) {
      Utils.notify(`Failed to load ${caseType} case: ${err.message}`, 'error');
    }
  }

  resolveCasePathInput(value) {
    const trimmed = (value || '').trim();
    if (!trimmed) return '';

    if (this.currentUserRoot && this.currentUserId && trimmed.startsWith(this.currentUserId)) {
      const relative = trimmed.slice(this.currentUserId.length).replace(/^\/+/, '');
      if (!relative) {
        return this.currentUserRoot;
      }
      const base = this.currentUserRoot.replace(/\/+$/, '');
      return `${base}/${relative}`;
    }

    return trimmed;
  }

  updateStats(stats) {
    if (this.elements.statPoints) this.elements.statPoints.textContent = stats.points.toLocaleString();
    if (this.elements.statCells) this.elements.statCells.textContent = stats.cells.toLocaleString();
    if (this.elements.statMemory) this.elements.statMemory.textContent = Utils.formatBytes(stats.memory);
    if (this.elements.statRenderTime) this.elements.statRenderTime.textContent = stats.renderTime.toFixed(1) + ' ms';
    if (this.elements.fpsCounter) this.elements.fpsCounter.textContent = `FPS: ${stats.fps}`;
  }

  updateTimeControls(currentIndex, total, times = []) {
    const slider = this.elements.timeSlider;
    const hasTimeline = total > 1;
    const clampedIndex = Math.min(Math.max(currentIndex, 0), Math.max(total - 1, 0));

    slider.max = Math.max(total - 1, 0);
    slider.value = clampedIndex;
    slider.step = 1;
    slider.disabled = !hasTimeline;

    const controlsToToggle = [
      this.elements.btnFirstFrame,
      this.elements.btnPrevFrame,
      this.elements.btnPlayPause,
      this.elements.btnNextFrame,
      this.elements.btnLastFrame
    ];
    controlsToToggle.forEach(btn => {
      if (btn) btn.disabled = total <= 1;
    });

    const currentTimeValue = Array.isArray(times) && times.length > 0 && clampedIndex < times.length
      ? Number(times[clampedIndex])
      : clampedIndex;
    const totalTimeValue = Array.isArray(times) && times.length > 0
      ? Number(times[times.length - 1])
      : (total > 0 ? total - 1 : 0);

    this.elements.currentTime.textContent = `${Number.isFinite(currentTimeValue) ? currentTimeValue.toFixed(3) : '0.000'}s`;
    this.elements.totalTime.textContent = `/ ${Number.isFinite(totalTimeValue) ? totalTimeValue.toFixed(3) : '0.000'}s`;
  }

  updateColormapPreview() {
    const preset = this.elements.colormapPreset.value;
    const dropdownId = 'colormapPreset';
    const gradient = Colormap.createGradientCSS(preset, 'to right', dropdownId);
    this.elements.colormapPreview.style.background = gradient;
  }

  updateSolidColorPickerVisibility() {
    const preset = this.elements.colormapPreset.value;
    const isSolid = Colormap.isSolidColor(preset);

    if (this.elements.solidColorPicker) {
      this.elements.solidColorPicker.style.display = isSolid ? 'flex' : 'none';
    }
  }

  toggleSidebar() {
    if (!this.elements.mainContent) return;

    const isHidden = this.elements.mainContent.classList.toggle('sidebar-hidden');

    // Save preference to localStorage
    localStorage.setItem('coandacloud-sidebar-hidden', isHidden);

    // Trigger resize event after transition to update WebGL canvas
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 350); // Match CSS transition duration (0.3s) + buffer
  }

  updatePipelineTree(items = [], selectedId = null) {
    // Pipeline Browser removed from UI - skip update
    if (!this.elements.pipelineTree) {
      return;
    }

    if (!Array.isArray(items) || items.length === 0) {
      this.elements.pipelineTree.innerHTML = '<div class="text-muted">No objects loaded</div>';
      return;
    }

    this.elements.pipelineTree.innerHTML = items.map(item => `
      <div class="pipeline-item ${item.id === selectedId ? 'active' : ''}" data-id="${item.id}">
        <span class="icon">${item.icon || '🧩'}</span>
        <span class="label">${item.name}</span>
        ${item.removable ? `<button class="pipeline-remove" data-id="${item.id}" title="Remove">&times;</button>` : ''}
      </div>
    `).join('');

    this.elements.pipelineTree.querySelectorAll('.pipeline-item').forEach(el => {
      const id = el.dataset.id;
      el.addEventListener('click', () => {
        if (this.viewer && typeof this.viewer.selectPipelineItem === 'function') {
          this.viewer.selectPipelineItem(id);
        }
      });
    });

    this.elements.pipelineTree.querySelectorAll('.pipeline-remove').forEach(btn => {
      const id = btn.dataset.id;
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (this.viewer && typeof this.viewer.removePipelineItem === 'function') {
          this.viewer.removePipelineItem(id);
        }
      });
    });
  }

  updatePropertiesPanel(properties) {
    // Properties panel removed - this is now a no-op
    return;
  }

  renderFilterProperties(props) {
    // Properties panel removed - this is now a no-op
    return;
  }

  renderFilterField(field, currentValue, props) {
    const value = currentValue ?? field.value ?? '';

    if (field.type === 'select') {
      const options = (field.choices || []).map(choice => `
        <option value="${choice.value}" ${choice.value === value ? 'selected' : ''}>
          ${choice.label}
        </option>
      `).join('');
      const pickButton = this.renderPickButton(field, props);
      return `
        <div class="property-control"${field.group ? ` data-group="${field.group}"` : ''}>
          <label for="filter-${field.name}">${field.label}</label>
          <select id="filter-${field.name}" name="${field.name}">
            ${options}
          </select>
          ${pickButton}
        </div>
      `;
    }

    if (field.type === 'number') {
      const minAttr = field.min != null ? `min="${field.min}"` : '';
      const maxAttr = field.max != null ? `max="${field.max}"` : '';
      const stepAttr = field.step != null ? `step="${field.step}"` : '';
      const pickButton = this.renderPickButton(field, props);
      return `
        <div class="property-control"${field.group ? ` data-group="${field.group}"` : ''}>
          <label for="filter-${field.name}">${field.label}</label>
          <input
            type="number"
            id="filter-${field.name}"
            name="${field.name}"
            value="${value}"
            ${minAttr}
            ${maxAttr}
            ${stepAttr}
          >
          ${pickButton}
        </div>
      `;
    }

    return `
      <div class="property-control"${field.group ? ` data-group="${field.group}"` : ''}>
        <label for="filter-${field.name}">${field.label}</label>
        <input type="text" id="filter-${field.name}" name="${field.name}" value="${value}">
        ${this.renderPickButton(field, props)}
      </div>
    `;
  }

  renderPickButton(field, props) {
    if (!field.pickGroup || !props) return '';
    if (this.renderedPickGroups.has(field.pickGroup)) return '';
    this.renderedPickGroups.add(field.pickGroup);
    const label = field.pickLabel || 'Pick';
    return `
      <button type="button" class="property-pick" data-filter-id="${props.id}" data-pick-group="${field.pickGroup}">
        ${label}
      </button>
    `;
  }

  attachFilterFormHandlers(form, props) {
    const variableSelect = form.querySelector('select[name="variable"]');
    if (variableSelect) {
      variableSelect.addEventListener('change', (event) => {
        this.handleFilterVariableChange(props, event.target.value, form);
      });
      this.handleFilterVariableChange(props, variableSelect.value, form);
    }

    const planeSelect = form.querySelector('select[name="plane"]');
    if (planeSelect) {
      planeSelect.addEventListener('change', (event) => {
        this.handleFilterPlaneChange(event.target.value, form);
      });
      this.handleFilterPlaneChange(planeSelect.value, form);
    }

    const seedTypeSelect = form.querySelector('select[name="seedType"]');
    if (seedTypeSelect) {
      const updateVisibility = () => {
        this.applyFilterGroupVisibility(form, props.kind, seedTypeSelect.value);
      };
      seedTypeSelect.addEventListener('change', updateVisibility);
      updateVisibility();
    } else {
      this.applyFilterGroupVisibility(form, props.kind);
    }
  }

  handleFilterVariableChange(props, variable, form) {
    if (!this.viewer || typeof this.viewer.getFieldRange !== 'function') {
      return;
    }
    const range = this.viewer.getFieldRange(variable);
    if (!range) {
      return;
    }

    const span = range.span || (range.max - range.min) || 1;

    if (props.kind === 'contour') {
      const valueInput = form.querySelector('input[name="value"]');
      if (valueInput) {
        valueInput.min = range.min;
        valueInput.max = range.max;
        valueInput.step = span / 200 || 0.01;
        const numericValue = parseFloat(valueInput.value);
        if (!Number.isFinite(numericValue) || numericValue < range.min || numericValue > range.max) {
          valueInput.value = (range.min + range.max) / 2;
        }
      }
    }

    if (props.kind === 'threshold') {
      const minInput = form.querySelector('input[name="min"]');
      const maxInput = form.querySelector('input[name="max"]');
      const step = span / 200 || 0.01;
      if (minInput) {
        minInput.min = range.min;
        minInput.max = range.max;
        minInput.step = step;
        if (!Number.isFinite(parseFloat(minInput.value))) {
          minInput.value = range.min;
        }
      }
      if (maxInput) {
        maxInput.min = range.min;
        maxInput.max = range.max;
        maxInput.step = step;
        if (!Number.isFinite(parseFloat(maxInput.value))) {
          maxInput.value = range.max;
        }
      }
    }
  }

  handleFilterPlaneChange(plane, form) {
    if (!this.viewer || typeof this.viewer.getAxisBounds !== 'function') {
      return;
    }
    const bounds = this.viewer.getAxisBounds(plane);
    const positionInput = form.querySelector('input[name="position"]');
    if (!positionInput || !bounds) return;

    positionInput.min = bounds.min;
    positionInput.max = bounds.max;
    positionInput.step = bounds.span / 200 || 0.01;

    const numericValue = parseFloat(positionInput.value);
    if (!Number.isFinite(numericValue) || numericValue < bounds.min || numericValue > bounds.max) {
      positionInput.value = bounds.center;
    }
  }

  applyFilterGroupVisibility(form, kind, seedType = null) {
    const groupedControls = form.querySelectorAll('.property-control[data-group]');
    if (groupedControls.length === 0) {
      return;
    }

    if (kind !== 'streamlines') {
      groupedControls.forEach(control => control.classList.remove('hidden'));
      return;
    }

    const activeGroup = seedType ? `seed-${seedType}` : 'seed-point';
    groupedControls.forEach(control => {
      const groups = (control.dataset.group || '').split(',').map(v => v.trim());
      if (groups.includes(activeGroup)) {
        control.classList.remove('hidden');
      } else {
        control.classList.add('hidden');
      }
    });
  }
}

// Export
window.Controls = Controls;
